// services/get-legislation-html.service.ts
import { BACKEND_URL, HOST } from '@/lib/config';
import documentServices from './document-services';

class GetLegislationHtmlServices {
  private resolvedIriCache: Map<string, string>;
  private signedUrlCache: Map<string, { url: string; expiry: number }>;

  constructor() {
    this.resolvedIriCache = new Map<string, string>();
    this.signedUrlCache = new Map<string, { url: string; expiry: number }>();
  }

  /**
   * Get legislation HTML content using signed URL from backend API
   * Since all buckets are now private, we need to get signed URLs from the backend
   */
  async getLegislationHTML(iri: string, documentId?: number): Promise<string> {
    const normalized = iri.startsWith('/') ? iri.slice(1) : iri;

    // Parse the IRI to extract base legislation IRI and any section identifiers
    const { baseIri, isPartial } = this.parseIriSegments(normalized);

    let finalIri = baseIri;
    if (isPartial) {
      // Partial IRI - need to get latest version from API
      finalIri = await this.getLatestVersionIri(`/${baseIri}`);
    }

    // Get signed URL from backend
    const signedUrl = await this.getSignedUrlForLegislation(
      finalIri,
      documentId
    );
    return this.fetchAndDecompressHtml(signedUrl);
  }

  /**
   * Get signed URL for a legislation document from the backend API
   * Uses caching with 50-minute expiry (signed URLs last 1 hour)
   */
  private async getSignedUrlForLegislation(
    iri: string,
    documentId?: number
  ): Promise<string> {
    const cacheKey = `legislation:${iri}`;

    // Check cache first (with expiry check)
    const cached = this.signedUrlCache.get(cacheKey);
    if (cached && Date.now() < cached.expiry) {
      console.log(
        `[getLegislationHtmlServices] ✅ Signed URL Cache HIT for ${iri}`
      );
      return cached.url;
    }

    console.log(
      `[getLegislationHtmlServices] ⬇️ Signed URL Cache MISS - Fetching from backend for ${iri}`
    );

    try {
      // If we have a documentId, use it directly
      if (documentId) {
        const response = await documentServices.getDocumentInfo(documentId);
        const signedUrl = response?.data?.data?.urls?.html_url;
        if (signedUrl) {
          // Cache for 50 minutes (signed URLs expire in 1 hour)
          this.signedUrlCache.set(cacheKey, {
            url: signedUrl,
            expiry: Date.now() + 50 * 60 * 1000,
          });
          return signedUrl;
        }
      }

      // Otherwise, use IRI to get document info
      const normalizedIri = iri.startsWith('/') ? iri.slice(1) : iri;
      const response = await documentServices.getDocumentInfo(normalizedIri);
      const signedUrl = response?.data?.data?.urls?.html_url;

      if (signedUrl) {
        // Cache for 50 minutes (signed URLs expire in 1 hour)
        this.signedUrlCache.set(cacheKey, {
          url: signedUrl,
          expiry: Date.now() + 50 * 60 * 1000,
        });
        return signedUrl;
      }

      throw new Error(`No signed URL returned for legislation IRI: ${iri}`);
    } catch (error) {
      console.error(
        `[getLegislationHtmlServices] Error getting signed URL for ${iri}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Parse IRI segments to extract base legislation IRI and determine if it's partial
   * Handles cases where section identifiers (paragraf-X, odsek-Y, etc.) are in the path
   *
   * Valid base IRI formats:
   * - SK/ZZ/YEAR/NUMBER (4 segments, partial)
   * - SK/ZZ/YEAR/NUMBER/DATE (5 segments, complete - DATE is 8 digits like 20231101)
   *
   * @returns Object with baseIri (cleaned) and isPartial flag
   */
  private parseIriSegments(normalized: string): {
    baseIri: string;
    isPartial: boolean;
  } {
    const segments = normalized.split('/').filter(Boolean);

    // Find where the base IRI ends (before any section identifiers)
    // Section identifiers start with: paragraf-, odsek-, pismeno-, bod-, cast-, hlava-, etc.
    const sectionPrefixes = [
      'paragraf-',
      'odsek-',
      'pismeno-',
      'bod-',
      'cast-',
      'hlava-',
      'diel-',
      'oddiel-',
      'clanok-',
      'priloha-',
    ];

    let baseSegmentCount = segments.length;
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i]?.toLowerCase() ?? '';
      if (sectionPrefixes.some((prefix) => segment.startsWith(prefix))) {
        baseSegmentCount = i;
        break;
      }
    }

    // Extract base segments
    const baseSegments = segments.slice(0, baseSegmentCount);
    const baseIri = baseSegments.join('/');

    // Determine if partial: exactly 4 segments is partial, 5 is complete
    // The 5th segment should be a date (8 digits like 20231101)
    const fifthSegment = baseSegments[4] ?? '';
    const isPartial =
      baseSegments.length === 4 ||
      (baseSegments.length >= 5 && !/^\d{8}$/.test(fifthSegment));

    // If we have more than 5 segments and the 5th is a date, keep only first 5
    let cleanBaseIri = baseIri;
    if (baseSegments.length >= 5 && /^\d{8}$/.test(fifthSegment)) {
      cleanBaseIri = baseSegments.slice(0, 5).join('/');
    }

    console.log(
      `[getLegislationHtmlServices] Parsed IRI: "${normalized}" -> base: "${cleanBaseIri}", isPartial: ${isPartial}`
    );

    return { baseIri: cleanBaseIri, isPartial };
  }

  /**
   * Get the latest version IRI for a partial IRI (without date segment)
   * Uses caching to avoid redundant API calls
   */
  private async getLatestVersionIri(partialIri: string): Promise<string> {
    // Normalize the key for caching (with leading slash)
    const cacheKey = partialIri.startsWith('/') ? partialIri : `/${partialIri}`;

    // Check cache first
    const cached = this.resolvedIriCache.get(cacheKey);
    if (cached) {
      console.log(
        `[getLegislationHtmlServices] ✅ IRI Cache HIT for ${partialIri} -> ${cached}`
      );
      return cached;
    }

    console.log(
      `[getLegislationHtmlServices] ⬇️ IRI Cache MISS - Fetching latest version for ${partialIri}`
    );

    try {
      const apiUrl = `${HOST}${BACKEND_URL.LEGISLATION.BASE}${BACKEND_URL.LEGISLATION.REDIRECT_LEGISLATION_IRI}?iri=${encodeURIComponent(cacheKey)}`;
      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'success' && data.data) {
        // Remove leading slash for storage path
        const fullIri = data.data.startsWith('/')
          ? data.data.slice(1)
          : data.data;

        // Cache the result
        this.resolvedIriCache.set(cacheKey, fullIri);
        console.log(
          `[getLegislationHtmlServices] 💾 Cached IRI resolution: ${partialIri} -> ${fullIri}`
        );

        return fullIri;
      }

      throw new Error(`No versions found for IRI: ${partialIri}`);
    } catch (error) {
      console.error(
        `[getLegislationHtmlServices] Error resolving IRI ${partialIri}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Get HTML content from a storage path (signed URL or direct path)
   * For signed URLs (starting with http), fetch directly
   * For storage paths, get signed URL from backend first
   */
  async getHtmlFromStoragePath(storagePath: string): Promise<string> {
    // If it's already a signed URL, fetch directly
    if (storagePath.startsWith('http')) {
      return this.fetchAndDecompressHtml(storagePath);
    }

    // For storage paths, we need to get the document and use the signed URL
    // This is typically called with a bucket_path like "html/judikaty/v1/secHtml/uuid.html"
    // We can't directly get a signed URL without a document ID, so try fetching directly
    // (this case should be rare as the backend now returns signed URLs)
    console.warn(
      `[getLegislationHtmlServices] getHtmlFromStoragePath called with non-URL path: ${storagePath}. This may fail as buckets are now private. Consider passing a signed URL instead.`
    );

    throw new Error(
      `Cannot fetch from storage path "${storagePath}" - buckets are private. Use signed URL from backend API.`
    );
  }

  private async fetchAndDecompressHtml(url: string): Promise<string> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);

    const supportsDS =
      typeof window !== 'undefined' && 'DecompressionStream' in window;

    const gzipStreamToText = async (readable: ReadableStream<Uint8Array>) => {
      const ds = new DecompressionStream('gzip');
      const decompressed = readable.pipeThrough(
        ds as TransformStream<Uint8Array, Uint8Array>
      );
      return new Response(decompressed).text();
    };

    const sniffAndMaybeDecompress = async () => {
      const buf = await res.arrayBuffer();
      const u8 = new Uint8Array(buf);

      const looksGzip = u8.length >= 2 && u8[0] === 0x1f && u8[1] === 0x8b;

      if (looksGzip && supportsDS) {
        const stream = new Response(u8).body!;
        return await gzipStreamToText(stream);
      }

      return new TextDecoder('utf-8').decode(u8);
    };

    let html: string;
    const encLower = res.headers.get('content-encoding')?.toLowerCase() ?? '';

    if (encLower.includes('gzip') && supportsDS && res.body) {
      html = await gzipStreamToText(res.body);
    } else {
      html = await sniffAndMaybeDecompress();
    }

    return this.sanitizeEuLegislationHtml(this.sanitizeSlovLexHtml(html));
  }

  /**
   * Wrap EUR-Lex CELLAR XHTML in a `.eu-legislation` container so the scoped
   * stylesheet (eu-legislation.css) can style its oj-* classes. Also strips
   * every upstream stylesheet <link> — relative refs (oj-convex-act.css) 404
   * when served from our domain, and absolute refs to
   * publications.europa.eu/resource/presentation-style-sheet/oj/oj.css also
   * 404 (path no longer exists on EUR-Lex). Our scoped eu-legislation.css
   * replaces them. No-op when the HTML doesn't contain EU markers — SK
   * legislation and decisions are returned unchanged.
   */
  sanitizeEuLegislationHtml(html: string): string {
    // Cheap detection: oj-ti-art (article titles) or oj-doc-ti (doc titles) are
    // present in every CELLAR act and absent from SK / decision HTML.
    if (
      !html.includes('class="oj-ti-art"') &&
      !html.includes('class="oj-doc-ti"')
    ) {
      return html;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Drop every stylesheet <link> the CELLAR doc carries — both relative
      // (oj-convex-*.css) and absolute (publications.europa.eu/...oj.css)
      // refs 404, and our scoped eu-legislation.css renders the oj-* classes.
      doc
        .querySelectorAll(
          'link[rel="stylesheet"], link[href*="publications.europa.eu"], link[href*="oj-convex"], link[href*="bootstrap"], link[href$=".css"]'
        )
        .forEach((el) => el.remove());

      const body = doc.body;
      if (!body) return html;

      const wrapper = doc.createElement('div');
      wrapper.className = 'eu-legislation';
      while (body.firstChild) {
        wrapper.appendChild(body.firstChild);
      }
      return wrapper.outerHTML;
    } catch (err) {
      console.error(
        '[getLegislationHtmlServices] Error sanitizing EU legislation HTML:',
        err
      );
      return html;
    }
  }

  /**
   * Sanitize HTML that may contain the full Slov-lex website structure.
   * Old legislation stored before the backend extraction logic was implemented
   * may contain the full website HTML (navigation, security notices, etc.)
   * instead of just the cleaned legislation content.
   *
   * This mirrors the backend extraction in fetch_legislation.py:get_html_page_data
   * which extracts div#predpis and its subsequent siblings from div#column-1.
   */
  sanitizeSlovLexHtml(html: string): string {
    // Quick check: if there's no "column-1" marker, the HTML is already clean
    if (!html.includes('id="column-1"') && !html.includes("id='column-1'")) {
      return html;
    }

    console.warn(
      '[getLegislationHtmlServices] Detected full Slov-lex website HTML, extracting legislation content...'
    );

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const column1 = doc.getElementById('column-1');
      if (!column1) return html;

      const predpis = column1.querySelector('#predpis');
      if (!predpis) return html;

      // Collect predpis element and all subsequent siblings (same as backend logic)
      const elements: Element[] = [predpis];
      let sibling = predpis.nextElementSibling;
      while (sibling) {
        elements.push(sibling);
        sibling = sibling.nextElementSibling;
      }

      // Wrap in a container div (same as backend)
      const wrapper = doc.createElement('div');
      for (const el of elements) {
        wrapper.appendChild(el.cloneNode(true));
      }

      return wrapper.innerHTML;
    } catch (err) {
      console.error(
        '[getLegislationHtmlServices] Error sanitizing Slov-lex HTML:',
        err
      );
      return html;
    }
  }
}

export default new GetLegislationHtmlServices();
